import React, { useEffect, useMemo, useState } from 'react';
import CompanyLogo from '../../assets/Care_Concierge_Logo.png'
import { Checkbox, Grid, TextField } from '@mui/material';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import moment from 'moment';
import { Spinner } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { CountryStatesInput } from '../../component/country_state_input_group';
import '../../styles/eInvoice.css';
import { env } from '../../config/env';

const styles = {
  container: {
    maxWidth: '60%',
    margin: '0 auto',
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: '1rem',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Arial, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#5A46AA',
  },
  subtext: {
    fontSize: '14px',
    color: '#5A46AA',
  },
  image: {
    height: '120px',
    width: 'auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#5A46AA',
  },
  line: {
    height: '4px',
    backgroundColor: '#5A46AA',
    margin: '8px 0',
  },
  paragraph: {
    fontSize: '14px',
    color: '#374151',
    marginTop: '8px',
  },
  formRow: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: '8px',
		marginLeft: '12px'
	},
	label: {
		width: '30%',
		fontSize: '14px',
		fontWeight: '500',
	},
	input: {
		width: '50%',
		borderRadius: '15px',
		padding: '5px 20px',
		paddingLeft: '20px',
		fontSize: '14px',
		outline: 'none',
		transition: 'border-color 0.3s ease',
		border: '1px solid #5A46AA',
	},
  addressDetailsInput: {
    width: '70%',
		borderRadius: '15px',
		padding: '5px 20px',
		paddingLeft: '20px',
		fontSize: '14px',
		outline: 'none',
		transition: 'border-color 0.3s ease',
		border: '1px solid #5A46AA',
  },
  addressInput: {
		width: '50%',
		borderRadius: '15px',
		padding: '5px 20px',
		fontSize: '14px',
		outline: 'none',
		transition: 'border-color 0.3s ease',
		border: '1px solid #5A46AA',
	},
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
		marginBottom: '20px',
		marginTop: '15px'
  },
  note: {
    fontSize: '14px',
    color: '#4B5563',
  },
  submitButton: {
    backgroundColor: '#5A46AA',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    marginTop: '16px',
    cursor: 'pointer',
    marginLeft: 'auto',
    alignSelf: 'flex-end'
  },
  addressGroup: {
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: 16,
    marginTop: 8,
    backgroundColor: '#f9f9f9',
  },
};

const EInvoiceForm = () => {
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState("")
	const [originalData, setOriginalData] = useState(null);
  const [linkExpired, setLinkExpired] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [customerId, setCustomerId] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    ic_no: '',
    correspondence_email: '',
    mobile: '',
    add_street: '',
    add_street2: '',
    add_city: '',
    add_postcode: '',
    add_country: '',
    add_state: '',
    tin: '',
    sst: '',
    apply_future: true,
    submitted_at: ''
  });

  const declaration = originalData?.submitted_at
  ? [
      "For the avoidance of doubt, this e-Invoice request process is implemented solely to comply with the latest requirements under the Malaysian Government’s e-Invoicing initiative.",
      "Any additional documentation, clarifications, or corrections related to the issuance of e-Invoices shall be handled separately and may incur administrative processing time.",
      "Thank you."
    ]
  : [
      "For the avoidance of doubt, this e-Invoice request process is implemented solely to comply with the latest requirements under the Malaysian Government’s e-Invoicing initiative.",
      "Any additional documentation, clarifications, or corrections related to the issuance of e-Invoices shall be handled separately and may incur administrative processing time.",
      "This e-Invoice request applies only to current and future orders and does not cover past orders.",
      "By clicking the Submit button, you certify that the information provided in this form is accurate and agree to promptly update any changes as necessary.",
      "Thank you."
    ];

  const note = originalData?.submitted_at
  ? [
      "Thank You for Reviewing Your e-Invoicing Details.",
      "According to our records, you have registered to receive e-Invoices for all current and future orders and purchases made with Care Concierge.",
      "Please note the following:",
      "During the E-Invoicing relaxation period provided by the Inland Revenue Board of Malaysia (IRBM), Care Concierge may issue consolidated e-Invoices rather than individual invoices per transaction. This is fully compliant with IRBM’s transitional guidelines.",
      "If you need to make any corrections or updates to your submitted information, please contact the designated Person-in-Charge (PIC).",
      "We appreciate your cooperation and continued support in aligning with this important national initiative."
    ]
  : [
      "As part of the Malaysian Government's initiative to enhance tax compliance and promote digitalization, e-Invoicing will become mandatory for businesses beginning in 2025, with phased implementation based on annual turnover.",
      "To support this requirement, Care Concierge is introducing an e-Invoice request process for our clients. Kindly complete the form below with the necessary details to ensure timely and accurate issuance of your e-Invoice in accordance with the Inland Revenue Board of Malaysia (IRBM) guidelines.",
      "Please note that during the E-Invoicing relaxation period provided by the IRBM, businesses are permitted to issue consolidated e-Invoices for all activities and transactions. As such, individual e-Invoices may not be provided for each transaction, even if requested by the buyer, as long as the consolidated e-Invoice is compliant with IRBM’s requirements."
    ]


const loadInfo = async (orderId) => {
  try {
    const response = await axios.get(
      `${env.API_ENDPOINT_URL}/careadmin/einvoiceinfo/${orderId}`
    );
    console.log(response)

    if (response.status == 200) {
      const data = response.data[0];
      const formattedData = {
        full_name: data.full_name,
        ic_no: data.ic_no,
        correspondence_email: data.correspondence_email,
        mobile: data.mobile,
        add_street: data.add_street,
        add_street2: data.add_street2,
        add_city: data.add_city,
        add_postcode: data.add_postcode,
        add_country: data.add_country,
        add_state: data.add_state,
        tin: data.tin,
        sst: data.sst,
        apply_future: data.apply_future,
        submitted_at: data.submitted_at
      }

      console.log(data.id ? true : false);
    
      setCustomerId(data.customer_id);
      setFormData(formattedData);
      setOriginalData(formattedData);
      setLinkExpired(moment(data.link_expires).format("DD/MM/YYYY"));
      if (
        data.link_expires &&
        moment.utc(data.link_expires).local().isSame(moment(), 'day')
      ) {
        navigate("/link-expired");
      } else {
        setIsLoading(false);
      }
    }
  } catch (error) {
    console.error('Failed to load invoice info:', error);
  }
};

	const decryption = (param) => {
		const params = (new URL(document.location)).searchParams;
		let encrypted = params.get(param);
		encrypted = encrypted?.replace(/ /g,"+");
		const decrypted = CryptoJS.AES.decrypt(encrypted, "CareConcierge").toString(CryptoJS.enc.Utf8);
		return decrypted;
	}

	useEffect(() => {
		const decryptedOrderId = decryption("order_id");
    loadInfo(decryptedOrderId);
    setOrderId(decryptedOrderId);
	}, []);

	const hasFormChanged = () => {
		if (!originalData) return false;
		for (const key in formData) {
			if (formData[key] !== originalData[key]) {
				return true;
			}
		}
		return false;
	};

  const handleChange = (e) => {
    const hasChanges = hasFormChanged();
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:  value,
    });
  };

  const handleAddressChange = (countryObj, stateObj) => {
    setFormData({
      ...formData,
      add_country: countryObj ? countryObj.value : '',
      add_state: stateObj ? stateObj.value : '',
    });
  };

  const handleSubmit = (e) => {
		e.preventDefault();
    updateChanges();
	};	

	const updateChanges = async () => {
    const payload = {
      payload : {
        ...formData,
        submitted_at: moment().format("YYYY-MM-DD HH:mm:ss")
      },
      auditDescription : {
        changes: "E-invoice Form (Link)",
        status: "Client updated E-invoice info",
        updated_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        updated_by: 0,
        updated_source: "externalLink",
        customer_id: customerId,
      }
    }
    console.log(payload)

		const response = await axios.put(`${env.API_ENDPOINT_URL}/careadmin/einvoiceinfo/${orderId}`, payload);
		if(response?.data?.affectedRows > 0){
      alert('Data saved for future used. We have received the application and will get back to you soon, thank you.')
      loadInfo(orderId);
		}else {
			console.error('Failed to submit form');
		}
	};

  const fields = [
    { label: '1. Name (As per IC) *', name: 'full_name', required: true },
    { label: '2. MyKad (IC) No *', name: 'ic_no', required: true },
    { label: '3. Mobile No *', name: 'mobile', required: true },
    { label: '4. Email *', name: 'correspondence_email', required: true },
    { label: '6. TIN (Optional)', name: 'tin' },
    { label: '7. SST (Optional)', name: 'sst' }
  ];

  const addressFields = [
    { label: 'Address Line 1 *', name: 'add_street', required: true },
    { label: 'Address Line 2', name: 'add_street2' },
    { label: 'City *', name: 'add_city', required: true },
    { label: 'Postcode *', name: 'add_postcode', required: true },
  ];

  return (
    isLoading?
    <center>
        <Spinner />
    </center>:
    <div style={styles.container} className='container-responsive'>
      <div style={styles.header} className="header-responsive">
        <div className="logo-container">
          <img src={CompanyLogo} alt="Company Logo" style={styles.image} />
        </div>
        <div className="header-text-responsive">
          <h1 style={styles.title}>Request for E-Invoice</h1>
          <p style={styles.subtext} className="header-subtext-responsive">Important Notice</p>
        </div>
      </div>

      <div style={{ paddingTop: '24px' }} className="section-padding-responsive">
        <div style={styles.sectionHeader} className="section-header-responsive">
          <h2 style={styles.sectionTitle}>
						{
							originalData.submitted_at?
							"Your e-Invoice registration with Care Concierge is complete.":"Implementation of E-Invoicing in Malaysia"
						}
					</h2>
          <span style={styles.subtext}>{`Link Expires: ${linkExpired}`}</span>
        </div>
        <div style={styles.line}></div>
        {note.map((text, index) => (
          <p key={index} style={styles.paragraph}>{text}</p>
        ))}
      </div>

      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>E-Invoice Information</h2>
        <span style={styles.subtext}>{`Order ID: ${orderId}`}</span>
      </div>
      <div style={styles.line}></div>

      <form onSubmit={handleSubmit} 
				style={{
				marginTop: '24px',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-end',
			}}>
        
        {fields.slice(0, 4).map(({ label, name, required, placeholder }) => {
          const parts = label.split('*');
          return (
            <div key={name} style={styles.formRow} className="form-row-column">
              <label style={styles.label}>
                {parts[0]}
                {required && <span style={{ color: 'red' }}>*</span>}
              </label>
              <input
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required={required}
                placeholder={placeholder}
                style={styles.input}
                disabled={name === 'correspondence_email' || originalData.submitted_at}
              />
            </div>
          )}
        )}

        <div style={styles.formRow} className="form-row-column">
          <label style={styles.label}>5. Correspondence Address</label>
          <div style={{ ...styles.addressInput, padding: 0, border: 'none' }} className="address-input-responsive">
            <div style={styles.addressGroup}>
            {addressFields.map(({ label, name, required, placeholder }) => {
              const parts = label.split('*');
              return (
                <div key={name} style={styles.formRow} className="form-row-column">
                  <label style={styles.label}>
                    {parts[0]}
                    {required && <span style={{ color: 'red' }}>*</span>}
                  </label>
                  <input
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    placeholder={placeholder}
                    style={styles.addressDetailsInput}
                    disabled={originalData.submitted_at}
                  />
                </div>
              );
            })}

              <div style={{ marginBottom: '10px' }} className="form-row-column">
                <CountryStatesInput
                  isCountry
                  isState
                  isRequired
                  defaultCountry={formData.add_country}
                  defaultState={formData.add_state}
                  onChange={handleAddressChange}
                  isDisabled={!!originalData.submitted_at}
                />
              </div>
            </div>
          </div>
        </div>


        {fields.slice(4).map(({ label, name, required, placeholder }) => (
          <div key={name} style={styles.formRow} className="form-row-column">
            <label style={styles.label}>{label}</label>
            <input
              name={name}
              value={formData[name]}
              onChange={handleChange}
              required={required}
              placeholder={placeholder}
              style={styles.input}
              disabled={originalData.submitted_at}
            />
          </div>
        ))}
        
        <p style={styles.paragraph}>
					Declaration
        </p>

        {declaration.map((text, index) => (
          <p key={index} style={styles.note}>{text}</p>
        ))}

				{
					!originalData.submitted_at &&
					<button type="submit" style={styles.submitButton}>Submit</button>
				}
      </form>
    </div>
  );
};

export default EInvoiceForm;
